import React, { useState, useEffect, useContext } from 'react';
import jobServices from 'ApiService/Placement/jobServices';
import headhrStudentService from 'ApiService/HeadHR/headhrStudentService';
import { AuthContext } from 'contexts/AuthContext';
import toast from 'react-hot-toast';

const AllStudents = () => {
  const { state } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [batches, setBatches] = useState([]);
  const [branch, setBranch] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [loadingBranch, setloadingBranch] = useState(true);
  const [jobPriorityFilter, setJobPriorityFilter] = useState('all');
  const [studentDetails, setStudentDetails] = useState([]);
  const [classData, setClassData] = useState({
    courseId: '',
    batchId: '',
  });
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const branchId = selectedBranch || state?.selectedBranch || null;
  // Only head HRs and super admins can resume a requested unpause
  const canUnpause =
    state?.user?.role === 'superadmin' ||
    (state?.user?.role === 'placement' && state?.user?.hrRole === 'headhr');
  const [unpauseStudent, setUnpauseStudent] = useState(null);
  const [unpauseReason, setUnpauseReason] = useState('');
  const [unpausing, setUnpausing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const isUnpauseRequested = (student) =>
    student.currentUnpauseRequestStatus === 'pending';

  const formatPauseDate = (date) =>
    !date || date === 'n/a'
      ? 'Open-ended'
      : new Date(date).toLocaleDateString();

  const renderRequestStatus = (status) => {
    const styles = {
      pending:
        'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
      approved:
        'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
      rejected: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
    };
    const labels = {
      pending: 'Unpause Requested',
      approved: 'Approved',
      rejected: 'Rejected',
    };
    if (!styles[status]) {
      return <span className="text-gray-500 dark:text-gray-400">None</span>;
    }
    return (
      <span
        className={`inline-block whitespace-nowrap rounded-full px-2 py-1 text-xs font-semibold ${styles[status]}`}
      >
        {labels[status]}
      </span>
    );
  };

  const handleUnpause = async () => {
    if (!unpauseReason.trim()) {
      toast.error('Please enter a reason.');
      return;
    }
    setUnpausing(true);
    try {
      const response = await headhrStudentService.unpausePlacement(
        unpauseStudent._id,
        unpauseReason.trim()
      );
      if (response.data.success) {
        toast.success(response.data.message || 'Placement resumed.');
        setUnpauseStudent(null);
        setUnpauseReason('');
        fetchPausedStudents();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to resume placement.'
      );
    } finally {
      setUnpausing(false);
    }
  };

  const handleCourseChange = (event) => {
    const courseId = event.target.value;
    const selectedCourse = courses.find(
      (course) => course.courseId === courseId
    );
    setBatches(selectedCourse?.batches);
    setClassData({ ...classData, courseId, batchId: '' });
  };

  const handleBatchChange = (event) => {
    const batchId = event.target.value;
    setClassData({ ...classData, batchId });
  };

  const handleBranchChange = (event) => {
    const branchId = event.target.value;
    setSelectedBranch(branchId);
  };

  const fetchCourses = async () => {
    setLoadingCourses(true);
    try {
      const response = await jobServices.getCourseBatch(branchId);

      if (response.data.success) {
        setCourses(response.data.courses);
      } else {
        setCourses([]);
        toast.error('Failed to fetch courses.');
      }
    } catch (error) {
      setCourses([]);
      toast.error(error?.response?.data?.message || 'Failed to fetch courses.');
    } finally {
      setLoadingCourses(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, [branchId]);

  const fetchBranch = async () => {
    setloadingBranch(true);
    try {
      const response = await jobServices.getBranch();
      if (response.data.success) {
        setBranch(response.data.allBranches || []);
      } else {
        toast.error('Failed to fetch Branches.');
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to fetch Branches.'
      );
    } finally {
      setloadingBranch(false);
    }
  };

  useEffect(() => {
    fetchBranch();
  }, []);

  async function fetchPausedStudents() {
    setLoading(true);
    try {
      const requestData = {
        courseId: classData.courseId || undefined,
        batchId: classData.batchId || undefined,
        branchId: selectedBranch || undefined,
      };

      const response = await jobServices.getAllPausedStudents(requestData);

      if (response.data.success) {
        const priorityOrder = { High: 1, Medium: 2, Low: 3 };
        // Students who requested an unpause come first
        const studentsWithSelection = response.data.data.sort(
          (a, b) =>
            isUnpauseRequested(b) - isUnpauseRequested(a) ||
            priorityOrder[a.placementPriority] -
              priorityOrder[b.placementPriority]
        );

        setStudentDetails(studentsWithSelection);
        setLoading(false);
      } else {
        setLoading(false);
        setStudentDetails([]);
        toast.error(response?.data?.message);
      }
    } catch (error) {
      setLoading(false);
      setStudentDetails([]);
      toast.error(error?.response?.data?.message);
    }
  }

  useEffect(() => {
    fetchPausedStudents();
  }, [classData.courseId, classData.batchId, selectedBranch, refreshKey]);

  useEffect(() => {
    let result = [...studentDetails];

    if (searchTerm) {
      result = result.filter(
        (student) =>
          student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          student.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          student.contactNumber
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
    }

    if (jobPriorityFilter !== 'all') {
      result = result.filter(
        (student) =>
          student.placementPriority?.toLowerCase() === jobPriorityFilter
      );
    }

    setFilteredData(result);
  }, [studentDetails, jobPriorityFilter, searchTerm]);

  function resetFilters() {
    setClassData({
      ...classData,
      courseId: '',
      batchId: '',
    });
    setSearchTerm('');
    setSelectedBranch('');
    setJobPriorityFilter('all');
    setRefreshKey((key) => key + 1);
  }

  return (
    <div className="w-full p-4 sm:overflow-auto">
      <div className="p-4">
        {/* Search and Filter Section */}
        <div className="flex flex-col items-center gap-4 sm:flex-row">
          <div className="w-full">
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
            />
          </div>

          <div className="w-full sm:w-72">
            <select
              value={jobPriorityFilter}
              onChange={(e) => setJobPriorityFilter(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
            >
              <option value="all">Job Priority</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          <button
            onClick={resetFilters}
            className="w-full rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:bg-blue-500 dark:hover:bg-blue-600 sm:w-40"
          >
            Reset
          </button>
        </div>

        {/* Filters Section */}
        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
          {state?.user?.role !== 'admin' && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-white">
                Select Branch
              </label>
              <select
                onChange={handleBranchChange}
                value={selectedBranch}
                className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
              >
                <option value="">Select Branch</option>
                {loadingBranch ? (
                  <option>Loading Branch...</option>
                ) : branch.length === 0 ? (
                  <option className="text-blue-600">No Branch available</option>
                ) : (
                  branch.map((item) => (
                    <option key={item._id} value={item._id}>
                      {item.name} ({item.location})
                    </option>
                  ))
                )}
              </select>
            </div>
          )}

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-white">
              Select Course
            </label>
            <select
              onChange={handleCourseChange}
              value={classData?.courseId}
              className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
            >
              <option value="">Select Course</option>
              {loadingCourses ? (
                <option>Loading courses...</option>
              ) : courses.length === 0 ? (
                <option className="text-blue-600">No courses available</option>
              ) : (
                courses.map((course, i) => (
                  <option key={course.courseId} value={course.courseId}>
                    {i + 1}. {course.courseName}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-white">
              Select Batch
            </label>
            <select
              onChange={handleBatchChange}
              value={classData.batchId}
              className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
            >
              <option value="">Select Batch</option>
              {batches.length > 0 ? (
                batches.map((batch) => (
                  <option key={batch._id} value={batch._id}>
                    Batch {batch.batchNumber} - From{' '}
                    {new Date(batch.startDate).toLocaleDateString()} to{' '}
                    {new Date(batch.endDate).toLocaleDateString()}
                  </option>
                ))
              ) : (
                <option>No batches available</option>
              )}
            </select>
          </div>
        </div>

        {/* Table Section */}
        {loading ? (
          <div className="flex h-40 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="mt-8 overflow-x-auto">
            {filteredData.length === 0 ? (
              <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                No paused students found
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr>
                    {[
                      'Sr No',
                      'Name',
                      'Gender',
                      'Email',
                      'Contact Number',
                      'Address',
                      'Priority',
                      'Pause Details',
                      'Unpause Request',
                      'Job History',
                      ...(canUnpause ? ['Action'] : []),
                    ].map((header) => (
                      <th
                        key={header}
                        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredData.map((student, index) => (
                    <tr
                      key={student._id}
                      className={
                        isUnpauseRequested(student)
                          ? 'bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/20 dark:hover:bg-amber-900/30'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                      }
                    >
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {index + 1}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {student.name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {student.gender}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {student.email}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {student.contactNumber}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {student.address
                          ? [
                              student.address.street,
                              student.address.city,
                              student.address.state,
                              student.address.postalCode,
                              student.address.country,
                            ]
                              .filter(Boolean)
                              .join(', ')
                          : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {student.placementPriority}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {student.pauseInfo && (
                          <div>
                            <p>
                              Start:{' '}
                              {formatPauseDate(student.pauseInfo.startDate)}
                            </p>
                            <p>
                              End: {formatPauseDate(student.pauseInfo.endDate)}
                            </p>
                            <p>Reason: {student.pauseInfo.reason}</p>
                            <p>
                              Paused by:{' '}
                              {student.pauseInfo.pausedBy ||
                                (student.pauseInfo.isPlacementPausedByAdmin
                                  ? 'Admin'
                                  : 'Student')}
                            </p>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {renderRequestStatus(
                          student.currentUnpauseRequestStatus
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {student.jobHistory && student.jobHistory.length > 0 ? (
                          <div className="space-y-2">
                            {student.jobHistory.map((job, idx) => (
                              <div
                                key={idx}
                                className="border-b border-gray-200 pb-2 last:border-b-0 dark:border-white/10"
                              >
                                <p className="font-medium">{job.jobTitle}</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  {job.companyName}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-500">
                                  Assigned:{' '}
                                  {new Date(
                                    job.assignedAt
                                  ).toLocaleDateString()}
                                </p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-500 dark:text-gray-400">
                            No job history
                          </p>
                        )}
                      </td>
                      {canUnpause && (
                        <td className="px-4 py-3 text-sm">
                          {isUnpauseRequested(student) ? (
                            <button
                              onClick={() => setUnpauseStudent(student)}
                              className="whitespace-nowrap rounded-lg bg-green-600 px-3 py-1.5 text-white hover:bg-green-700"
                            >
                              Unpause
                            </button>
                          ) : (
                            <span className="text-gray-500 dark:text-gray-400">
                              -
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {unpauseStudent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg dark:bg-navy-800">
            <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
              Resume placement for {unpauseStudent.name}?
            </h3>
            <textarea
              value={unpauseReason}
              onChange={(e) => setUnpauseReason(e.target.value)}
              placeholder="Reason for unpausing"
              rows={3}
              className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-white/10 dark:bg-navy-900 dark:text-white"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => {
                  setUnpauseStudent(null);
                  setUnpauseReason('');
                }}
                disabled={unpausing}
                className="rounded-lg bg-gray-200 px-4 py-2 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleUnpause}
                disabled={unpausing}
                className="rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700 disabled:opacity-60"
              >
                {unpausing ? 'Resuming...' : 'Resume Placement'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllStudents;
